import { Button } from 'components/atoms/Button';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import { useCallback, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { logAmplitude } from 'utils/amplitude';
import { WEB_AICATEGORYGUESSER_USERCHOSESUGGESTEDCATEGORY } from 'utils/amplitude/events/ai.amplitude';
import AIChatSpinner from '../AIChat/AIChatMessageSkeleton';
import { AISuggestCategoryProps } from './AISuggestCategory.types';
import { useCategoryReason } from './useCategoryReason';
import { usePredictCategory } from './usePredictCategory';

const mdComponentsWithProfession = (
  profession: string | undefined,
  categoryName: string,
) => ({
  strong: ({ children }: { children: React.ReactNode }) => {
    const text =
      Array.isArray(children) && typeof children[0] === 'string'
        ? children[0]
        : children;

    if (typeof text !== 'string') return null;
    const isProfession = text === profession;

    return isProfession || (text !== '-' && categoryName) ? (
      <span className="font-bold">{text}</span>
    ) : (
      <span className="bg-primary-700 animate-pulse h-3 w-20 inline-block rounded translate-y-0.5"></span>
    );
  },
});

const AISuggestCategory: FC<AISuggestCategoryProps> = ({
  searchValue,
  categoriesDict,
  onChooseCategory,
  formValues,
}) => {
  const { t } = useTranslation();
  const { data: user } = useGetCurrentCustomerData();
  const { APC1, APC2, country } = user || {};
  const profession = APC2
    ? t(`${country}.APC2.${APC2}`)
    : APC1
    ? t(`${country}.APC1.${APC1}`)
    : undefined;

  const predictedSearch = usePredictCategory({
    searchValue,
    categoriesDict,
  });

  const guessedCategoryReason = useCategoryReason(formValues, categoriesDict);

  const onChoose = useCallback(() => {
    if (!predictedSearch.category) return;

    logAmplitude(WEB_AICATEGORYGUESSER_USERCHOSESUGGESTEDCATEGORY, {
      query: searchValue,
      category_id: predictedSearch.category?.id,
      category_name:
        predictedSearch.category?.displayName ||
        predictedSearch.category?.title,
    });
    onChooseCategory?.(predictedSearch.category);
  }, [onChooseCategory, searchValue, predictedSearch.category]);

  const validSearchValue =
    searchValue?.trim()?.length > 2 && !predictedSearch.isError;
  if (!validSearchValue && !guessedCategoryReason) return null;

  return (
    <div className="flex flex-row items-start gap-3 rounded-2xl px-4 py-3 bg-primary-25 shadow-[0_0_0_1px_#EBE8ED,0_1px_2px_0_rgba(93,72,112,0.12)] backdrop-blur-[12px] mb-4 mt-2">
      <div>
        <AIChatSpinner size="small" />
      </div>

      <div className="flex flex-col space-y-2">
        <p className="text-sm font-semibold text-primary">
          {t('ai.suggest_category.title')}
        </p>
        {validSearchValue && (
          <>
            <ReactMarkdown
              className="text-sm font-medium text-primary-700"
              components={mdComponentsWithProfession(
                profession,
                predictedSearch.categoryName,
              )}
            >
              {t(
                profession
                  ? 'ai.suggest_category.subtitle_profession'
                  : 'ai.suggest_category.subtitle_no_profession',
                { ...predictedSearch, profession },
              )}
            </ReactMarkdown>
            {predictedSearch.notDeductible && (
              <p className="text-sm font-medium text-primary-700">
                {t('ai.suggest_category.caution')}
              </p>
            )}
          </>
        )}

        {!validSearchValue && guessedCategoryReason && (
          <ReactMarkdown
            className="text-sm font-medium text-primary-700"
            components={{
              strong: ({ children }: { children: React.ReactNode }) => (
                <span className="font-bold">{children}</span>
              ),
            }}
          >
            {t(guessedCategoryReason.key, guessedCategoryReason.params)}
          </ReactMarkdown>
        )}

        <div className="flex flex-row space-x-2 mt-2">
          {validSearchValue && (
            <Button
              structure="text"
              color="main"
              className="!text-primary hover:!text-primary-700"
              onClick={onChoose}
            >
              {predictedSearch.categoryName &&
              predictedSearch.categoryName !== '-' ? (
                t('ai.suggest_category.choose_category', {
                  categoryName: predictedSearch.categoryName,
                })
              ) : (
                <div className="flex gap-1 py-1">
                  <span className="bg-primary-700 animate-pulse h-3 w-16 rounded" />
                  <span className="bg-primary-700 animate-pulse h-3 w-20 rounded" />
                </div>
              )}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AISuggestCategory;
